import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import { DuplicatePanel } from './DuplicatePanel'
import type { DuplicateMatch } from './counterpartyApi'
import { fetchMergePreview, mergeCounterparty } from './counterpartyApi'
import { ConfirmModal } from './record'
import type { CardTab, CardTabContext } from './types'

/**
 * Дубли на карточке контрагента и слияние их в эту карточку (тикет #99).
 *
 * Слияние необратимо, поэтому идёт в два шага: сначала предпросмотр — что переедет, что
 * мешает и почему карточки несовместимы, — и только потом подтверждение. Кнопка без
 * предпросмотра сливала бы вслепую: пользователь узнал бы о «выставленном счёте, который
 * не переносится» отказом, а о переехавших сделках — уже после.
 */

function MergeAction({
  survivorId,
  match,
  onMerged,
}: {
  survivorId: string
  match: DuplicateMatch
  onMerged: (name: string) => void
}) {
  const { t } = useApp()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  const preview = useQuery({
    queryKey: ['merge-preview', survivorId, match.id],
    queryFn: ({ signal }) => fetchMergePreview(survivorId, match.id, signal),
    enabled: open,
    retry: false,
  })

  const merge = useMutation({
    mutationFn: () => mergeCounterparty(survivorId, match.id),
    // The confirmation is reported to the tab, not kept here: a merged duplicate drops out
    // of the list, this row unmounts with it, and a notice living in it would vanish
    // before anyone read it — the card would look as if the duplicate just disappeared.
    onSuccess: () => {
      onMerged(match.name)
      setOpen(false)
      void queryClient.invalidateQueries()
    },
    onError: (error) => setNotice(error instanceof ApiError ? error.message : t('app.error')),
  })

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} aria-label={`${t('merge.start')} ${match.name}`}>
        {t('merge.start')}
      </button>
    )
  }
  if (preview.isLoading) return <p>{t('app.loading')}</p>
  if (preview.error) {
    return <p role="alert">{preview.error instanceof ApiError ? preview.error.message : t('app.error')}</p>
  }
  const data = preview.data
  if (!data) return null
  const blocked = data.incompatible.length > 0 || Object.keys(data.blockers).length > 0

  return (
    <div className="card-gap" role="group" aria-label={t('merge.preview')}>
      <p>{t('merge.into', { duplicate: data.duplicateName, survivor: data.survivorName })}</p>
      {data.incompatible.length > 0 && (
        <ul role="alert">
          {data.incompatible.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      )}
      {Object.keys(data.blockers).length > 0 && (
        <div role="alert">
          <p>{t('merge.blocked')}</p>
          <ul>
            {Object.entries(data.blockers).map(([key, count]) => (
              <li key={key}>
                {t(`merge.ref.${key}`)}: {count}
              </li>
            ))}
          </ul>
        </div>
      )}
      {!blocked && (
        <>
          <p>{Object.keys(data.moved).length === 0 ? t('merge.nothingMoves') : t('merge.moves')}</p>
          <ul>
            {Object.entries(data.moved).map(([key, count]) => (
              <li key={key}>
                {t(`merge.ref.${key}`)}: {count}
              </li>
            ))}
          </ul>
        </>
      )}
      {notice && <p role="alert">{notice}</p>}
      <div className="report-actions">
        {/* The preview says what moves; the dialog asks once more, because the duplicate
            card is deleted and nothing on this screen brings it back. */}
        <button type="button" disabled={blocked || merge.isPending} onClick={() => setConfirming(true)}>
          {t('merge.confirm')}
        </button>
        <button type="button" onClick={() => setOpen(false)}>
          {t('admin.cancel')}
        </button>
      </div>
      <ConfirmModal
        open={confirming}
        icon="alert"
        title={t('merge.dialog.title', { duplicate: data.duplicateName, survivor: data.survivorName })}
        confirmLabel={t('merge.confirm')}
        pending={merge.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false)
          merge.mutate()
        }}
      >
        <p>{t('merge.dialog.text')}</p>
        <p className="strong" style={{ color: 'var(--fg)' }}>
          {t('record.irreversible')}
        </p>
      </ConfirmModal>
    </div>
  )
}

function DuplicatesOfCard({ context }: { context: CardTabContext }) {
  const { t, user } = useApp()
  const [merged, setMerged] = useState<string | null>(null)
  const values = context.record?.values ?? {}
  // Слияние для дубля — удаление: без обоих прав кнопки нет, сервер всё равно откажет.
  const mayMerge = can(user, 'sales.counterparty', 'update') && can(user, 'sales.counterparty', 'delete')
  const criteria = {
    inn: (values.inn as string | undefined) || undefined,
    phone: (values.phone as string | undefined) || undefined,
    email: (values.email as string | undefined) || undefined,
  }
  return (
    <div className="card-gap">
      <p className="muted">{t('merge.hint')}</p>
      {merged && (
        <p className="notice" role="status">
          {t('merge.done', { name: merged })}
        </p>
      )}
      <DuplicatePanel
        criteria={criteria}
        excludeId={context.recordId}
        renderAction={mayMerge ? (match) => <MergeAction survivorId={context.recordId} match={match} onMerged={setMerged} /> : undefined}
      />
    </div>
  )
}

export function duplicatesTab(): CardTab {
  return {
    id: 'duplicates',
    labelKey: 'merge.tab',
    render: (context: CardTabContext) => <DuplicatesOfCard context={context} />,
  }
}
